import { Link } from "react-router-dom";
import { Head } from "@unhead/react";

export default function SmartDca() {
  const title =
    "Smart DCA: Smarter Bitcoin Dollar-Cost Averaging | SatStacker";

  const description =
    "Learn how Smart DCA works, how it differs from regular Bitcoin dollar-cost averaging, and how SatStacker uses Smart Timing within recurring buying windows.";

  const canonical = "https://satstacker.app/smart-dca";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Smart DCA: Smarter Bitcoin Dollar-Cost Averaging",
    description,
    mainEntityOfPage: canonical,
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
  };

  return (
    <>
      <Head>
        <title>{title}</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={description}
        />
        <meta property="og:url" content={canonical} />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={title}
        />
        <meta
          name="twitter:description"
          content={description}
        />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Head>

      <main className="min-h-screen bg-zinc-950 text-white">
        <article className="max-w-3xl mx-auto px-5 py-16">

          <Link
            to="/bitcoin-dca"
            className="text-sm text-zinc-500 hover:text-orange-400"
          >
            ← Bitcoin DCA Guide
          </Link>

          <header className="mt-10 mb-12">
            <p className="text-sm uppercase tracking-widest text-orange-400 mb-4">
              Smart DCA Guide
            </p>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Smart DCA: A Smarter Approach to Bitcoin Dollar-Cost Averaging
            </h1>

            <p className="text-lg text-zinc-400 leading-relaxed">
              Smart DCA keeps the discipline of recurring Bitcoin purchases
              while adding rules around when a buy executes inside each
              scheduled buying window.
            </p>
          </header>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              What is Smart DCA?
            </h2>

            <p className="text-zinc-400 leading-7">
              Traditional dollar-cost averaging usually means buying a fixed
              dollar amount on a fixed schedule. The purchase happens whether
              Bitcoin is up, down, or unchanged at that moment.
            </p>

            <p className="text-zinc-400 leading-7">
              Smart DCA keeps the recurring budget and schedule but introduces
              timing rules inside the buying period. Instead of treating one
              arbitrary moment as the only opportunity to buy, the strategy
              can watch price movement during the window before executing.
            </p>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Regular DCA vs Smart DCA
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
                <p className="text-sm uppercase tracking-widest text-zinc-500 mb-3">
                  Regular DCA
                </p>

                <p className="text-zinc-400 leading-7">
                  Invests a predetermined amount at a predetermined time,
                  regardless of short-term Bitcoin price movement.
                </p>
              </div>

              <div className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5">
                <p className="text-sm uppercase tracking-widest text-orange-400 mb-3">
                  Smart DCA
                </p>

                <p className="text-zinc-400 leading-7">
                  Keeps the recurring investment plan but uses the buying
                  window to look for more favorable execution opportunities
                  before the window ends.
                </p>
              </div>
            </div>

            <p className="text-zinc-400 leading-7">
              The important distinction is that Smart DCA is not about
              constantly changing how much money you plan to invest. The
              recurring budget remains the foundation of the strategy.
            </p>
          </section>

          <section className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-6 mb-12">
            <p className="text-sm uppercase tracking-widest text-orange-400 mb-2">
              See the difference
            </p>

            <h2 className="text-2xl font-bold mb-3">
              Compare Smart Timing with regular Bitcoin DCA
            </h2>

            <p className="text-zinc-400 leading-7 mb-5">
              Enter your normal Bitcoin buying schedule and investment amount.
              SatStacker's calculator compares standard DCA with Smart Timing
              using historical Bitcoin price data.
            </p>

            <Link
              to="/calculator"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-xl"
            >
              Try the Bitcoin DCA Calculator →
            </Link>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              How SatStacker Smart Timing works
            </h2>

            <p className="text-zinc-400 leading-7">
              SatStacker's Smart Timing is designed around recurring buying
              windows. You choose your budget and whether you want a daily,
              weekly, or bi-weekly plan.
            </p>

            <p className="text-zinc-400 leading-7">
              During that window, SatStacker monitors Bitcoin price movement
              rather than automatically spending the entire allocation at one
              predetermined moment.
            </p>

            <p className="text-zinc-400 leading-7">
              The goal is straightforward: preserve the discipline of DCA while
              giving the same recurring budget more opportunity to execute
              during favorable price movement.
            </p>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              What happens if Bitcoin does not dip?
            </h2>

            <p className="text-zinc-400 leading-7">
              Smart DCA still needs a way to complete the recurring plan.
              Waiting indefinitely would turn a disciplined DCA strategy into
              an open-ended attempt to predict the market.
            </p>

            <p className="text-zinc-400 leading-7">
              SatStacker's Smart Timing therefore operates within a defined
              buying window. The objective is to improve execution inside that
              window, not to postpone a planned Bitcoin purchase forever.
            </p>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Is Smart DCA the same as market timing?
            </h2>

            <p className="text-zinc-400 leading-7">
              Not in the traditional sense. Market timing usually involves
              deciding when to enter or exit the market based on a prediction
              about where price will go next.
            </p>

            <p className="text-zinc-400 leading-7">
              Smart DCA starts with a recurring investment decision that has
              already been made. The timing logic operates inside that
              predetermined buying window rather than deciding whether you
              should invest at all.
            </p>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Does Smart DCA actually outperform regular DCA?
            </h2>

            <p className="text-zinc-400 leading-7">
              The answer depends on the market period and the prices available
              inside each buying window. A timing strategy cannot guarantee
              that every purchase will beat a fixed DCA purchase.
            </p>

            <p className="text-zinc-400 leading-7">
              That is why SatStacker compares the strategies using historical
              Bitcoin price data rather than assuming Smart Timing will always
              produce a better result.
            </p>

            <Link
              to="/blog/smart-dca-vs-regular-dca"
              className="text-orange-400 hover:underline"
            >
              See the Smart DCA vs regular DCA case study →
            </Link>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Smart DCA and buying frequency
            </h2>

            <p className="text-zinc-400 leading-7">
              The length of a buying window affects how much time a Smart DCA
              strategy has to observe price movement. Daily, weekly, and
              bi-weekly schedules therefore behave differently.
            </p>

            <p className="text-zinc-400 leading-7">
              The right cadence depends on your budget, preferences, and how
              frequently you want to allocate money to Bitcoin. A longer
              schedule is not automatically better for every person or every
              market environment.
            </p>

            <Link
              to="/bitcoin-dca"
              className="text-orange-400 hover:underline"
            >
              Learn more about Bitcoin DCA schedules →
            </Link>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Smart DCA vs increasing your investment
            </h2>

            <p className="text-zinc-400 leading-7">
              A meaningful comparison between Smart DCA and regular DCA should
              use the same investment budget.
            </p>

            <p className="text-zinc-400 leading-7">
              Simply spending more money during a dip can result in more
              Bitcoin, but that is different from improving the execution of
              the budget you had already planned to invest.
            </p>

            <p className="text-zinc-400 leading-7">
              SatStacker's Smart Timing is built around optimizing recurring
              purchases without requiring you to continually increase your
              planned budget.
            </p>

            <Link
              to="/blog/smart-timing-vs-supercharged"
              className="text-orange-400 hover:underline"
            >
              Compare Smart Timing with Supercharged buys →
            </Link>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Automating Smart DCA with SatStacker
            </h2>

            <p className="text-zinc-400 leading-7">
              SatStacker automates the process so you do not have to repeatedly
              watch Bitcoin charts or manually decide when to place each
              recurring purchase.
            </p>

            <p className="text-zinc-400 leading-7">
              You choose your schedule and budget, securely connect your Strike
              account, and SatStacker applies the selected timing strategy.
            </p>

            <p className="text-zinc-400 leading-7">
              SatStacker does not custody your funds. Bitcoin purchased through
              the workflow goes to your configured wallet.
            </p>
          </section>

          <section className="space-y-5 mb-12">
            <h2 className="text-2xl font-bold">
              Is Smart DCA right for everyone?
            </h2>

            <p className="text-zinc-400 leading-7">
              No investment strategy removes Bitcoin's volatility or guarantees
              better future results. Smart DCA is primarily designed for people
              who already want to accumulate Bitcoin on a recurring schedule
              and prefer a rules-based approach to execution.
            </p>

            <p className="text-zinc-400 leading-7">
              Historical comparisons can show how a strategy behaved in past
              markets, but they cannot predict future Bitcoin prices or ensure
              that Smart Timing will outperform standard DCA.
            </p>
          </section>

          <section className="border-t border-zinc-800 pt-10">
            <h2 className="text-2xl font-bold mb-6">
              Continue learning
            </h2>

            <div className="space-y-3">
              <Link
                to="/bitcoin-dca"
                className="block text-orange-400 hover:underline"
              >
                Bitcoin DCA Guide →
              </Link>

              <Link
                to="/calculator"
                className="block text-orange-400 hover:underline"
              >
                Bitcoin DCA Calculator →
              </Link>

              <Link
                to="/blog/smart-dca-vs-regular-dca"
                className="block text-orange-400 hover:underline"
              >
                Smart DCA vs Regular DCA Case Study →
              </Link>

              <Link
                to="/blog/smart-timing-vs-supercharged"
                className="block text-orange-400 hover:underline"
              >
                Smart Timing vs Supercharged DCA →
              </Link>

              <Link
                to="/blog/best-way-to-buy-bitcoin"
                className="block text-orange-400 hover:underline"
              >
                Best Way to Buy Bitcoin →
              </Link>
            </div>
          </section>

        </article>
      </main>
    </>
  );
}